import {
  Bell,
  CreditCard,
  FileBarChart2,
  Mail,
  Shield,
  Smartphone,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { EmptyState, ErrorState } from '@/components/shared/states';
import { cn } from '@/utils/cn';
import { formatRelative } from '@/utils/format';
import type { ConsoleNotification, ConsoleNotificationKind } from '@/lib/notifications';
import { resolveConsoleNotificationLink } from '@/lib/notification-navigation';

const KIND_ICONS: Record<ConsoleNotificationKind, LucideIcon> = {
  report: FileBarChart2,
  join_request: UserPlus,
  billing: CreditCard,
  sms_credits: Mail,
  payment_connection: Smartphone,
  campaign_delivery: Mail,
  security: Shield,
  other: Bell,
};

export const NotificationListItem = ({
  notification,
  compact = false,
  onOpen,
}: {
  notification: ConsoleNotification;
  compact?: boolean;
  onOpen?: (notification: ConsoleNotification) => void;
}) => {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const Icon = KIND_ICONS[notification.kind] ?? Bell;
  const unread = notification.readAt == null;

  const open = () => {
    onOpen?.(notification);
    const target = resolveConsoleNotificationLink(notification.deepLink);
    if (target) {
      void navigate(target);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      className={cn(
        'flex w-full gap-3 rounded-md px-3 py-2.5 text-left transition-colors',
        'hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
        unread && 'bg-brand/5',
        compact && 'px-2 py-2',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
          unread ? 'bg-brand/10 text-brand' : 'bg-surface-sunken text-fg-tertiary',
        )}
        aria-hidden
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start justify-between gap-2">
          <span className={cn('text-body-sm text-fg', unread && 'font-medium')}>
            {notification.title}
          </span>
          {unread ? (
            <span className="mt-1 size-2 shrink-0 rounded-full bg-brand" aria-hidden />
          ) : null}
        </span>
        <span className="mt-0.5 line-clamp-2 text-caption text-fg-secondary">{notification.body}</span>
        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-caption text-fg-tertiary">
          <span>{t(`kind.${notification.kind}`)}</span>
          <span aria-hidden>·</span>
          <time dateTime={notification.createdAt}>{formatRelative(notification.createdAt)}</time>
        </span>
      </span>
    </button>
  );
};

export const NotificationListStates = ({
  loading,
  error,
  empty,
  onRetry,
}: {
  loading?: boolean;
  error?: boolean;
  empty?: boolean;
  onRetry?: () => void;
}) => {
  const { t } = useTranslation('notifications');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" role="status">
        <span className="size-5 animate-spin rounded-full border-2 border-border border-t-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        title={t('error.title')}
        onRetry={onRetry}
        retryLabel={onRetry ? t('error.retry') : undefined}
      />
    );
  }

  if (empty) {
    return (
      <EmptyState
        icon={<Bell className="size-6" />}
        title={t('empty.title')}
        body={t('empty.body')}
      />
    );
  }

  return null;
};
